import {
  MessageCircle,
  Calendar,
  ClipboardCheck,
  FileText,
  Phone,
  ThumbsUp,
  BookOpen,
  User,
  Bot,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useTouchpointHistory,
  Touchpoint,
  TouchpointType,
  TouchpointInitiator,
} from '@/hooks/use-touchpoint-tracking';
import { cn } from '@/lib/utils';
import { format, parseISO, isToday, isYesterday } from 'date-fns';

interface TouchpointHistoryListProps {
  clientId: string;
  days?: number;
  maxItems?: number;
  className?: string;
}

const TYPE_CONFIG: Record<
  TouchpointType,
  { icon: React.ComponentType<{ className?: string }>; label: string; color: string }
> = {
  message: { icon: MessageCircle, label: 'Message', color: 'bg-info/15 text-info' },
  session: { icon: Calendar, label: 'Session', color: 'bg-success/15 text-success' },
  check_in: { icon: ClipboardCheck, label: 'Check-in', color: 'bg-chart-4/15 text-chart-4' },
  program_update: { icon: BookOpen, label: 'Program', color: 'bg-warning/15 text-warning' },
  note: { icon: FileText, label: 'Note', color: 'bg-muted text-muted-foreground' },
  call: { icon: Phone, label: 'Call', color: 'bg-info/15 text-info' },
  feedback: { icon: ThumbsUp, label: 'Feedback', color: 'bg-chart-8/15 text-chart-8' },
};

const INITIATOR_CONFIG: Record<
  TouchpointInitiator,
  { icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  trainer: { icon: User, label: 'Trainer' },
  client: { icon: User, label: 'Client' },
  system: { icon: Bot, label: 'Auto' },
};

function formatDateHeader(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEEE, MMM d');
}

function groupByDate(touchpoints: Touchpoint[]): Record<string, Touchpoint[]> {
  return touchpoints.reduce(
    (acc, tp) => {
      const dateKey = tp.touchpointDate;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(tp);
      return acc;
    },
    {} as Record<string, Touchpoint[]>
  );
}

function TouchpointItem({ touchpoint }: { touchpoint: Touchpoint }) {
  const typeConfig = TYPE_CONFIG[touchpoint.touchpointType];
  const initiatorConfig = INITIATOR_CONFIG[touchpoint.initiatedBy];
  const Icon = typeConfig.icon;
  const InitiatorIcon = initiatorConfig.icon;

  const preview = touchpoint.metadata?.preview as string | undefined;
  const sessionNumber = touchpoint.metadata?.session_number as number | undefined;

  return (
    <div className="flex items-start gap-3 py-2">
      <div className={cn('p-2 rounded-lg', typeConfig.color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{typeConfig.label}</span>
          <Badge variant="outline" className="text-xs px-1 py-0">
            <InitiatorIcon className="h-3 w-3 mr-1" />
            {initiatorConfig.label}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {format(parseISO(touchpoint.createdAt), 'h:mm a')}
          </span>
        </div>
        {preview && (
          <p className="text-sm text-muted-foreground truncate mt-0.5">
            {preview}
          </p>
        )}
        {sessionNumber && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Session #{sessionNumber}
          </p>
        )}
      </div>
    </div>
  );
}

export function TouchpointHistoryList({
  clientId,
  days = 30,
  maxItems = 20,
  className,
}: TouchpointHistoryListProps) {
  const { touchpoints, loading, error } = useTouchpointHistory(clientId, { days });

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-40" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null;
  }

  const grouped = groupByDate(touchpoints.slice(0, maxItems));
  const sortedDates = Object.keys(grouped).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <Card className={cn('border', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span>Recent Activity</span>
          <Badge variant="secondary" className="text-xs">
            {touchpoints.length} in {days} days
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {touchpoints.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-20" />
            <p className="text-sm">No touchpoints recorded</p>
            <p className="text-xs">Activity will appear here as you interact</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedDates.map((date) => (
              <div key={date}>
                <div className="text-xs font-medium text-muted-foreground mb-2 sticky top-0 bg-card py-1">
                  {formatDateHeader(date)}
                </div>
                <div className="space-y-1 divide-y">
                  {grouped[date].map((tp) => (
                    <TouchpointItem key={tp.id} touchpoint={tp} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
